import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ChevronRight, Download, Plus, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { formatBytes, UploadButton } from '@/components/tickets';
import { Button } from '@/components/ui/button';
import { Field, PageHeader } from '@/components/ui/form-field';
import { Alert, Badge, Card, CardContent, CardFooter, CardHeader, CardTitle, Input, Skeleton, Textarea } from '@/components/ui/primitives';
import { Pagination } from '@/components/ui/table';
import { api, ApiError, errorMessage } from '@/lib/api';
import type { Paginated, PortalTicketDetail, PortalTicketItem, StatusCategory } from '@/lib/types';
import { cn, formatDate, formatDateTime, formatRelative } from '@/lib/utils';
import { usePortalMe } from './portal-shell';
import { usePortalForms } from './portal-forms';
import { PortalAppointmentsCard } from '@/components/portal-appointments';

const STATUS_VARIANT: Record<StatusCategory, 'default' | 'warning' | 'success' | 'secondary'> = {
  NEW: 'default',
  OPEN: 'default',
  WAITING: 'warning',
  RESOLVED: 'success',
  CLOSED: 'secondary',
};

export function PortalStatus({ status, isClosed }: { status: PortalTicketItem['status']; isClosed: boolean }) {
  if (!status) return isClosed ? <Badge variant="secondary">Abgeschlossen</Badge> : null;
  return <Badge variant={STATUS_VARIANT[status.category]}>{status.label}</Badge>;
}

export function PortalHomePage() {
  const { data: me } = usePortalMe();
  const [state, setState] = useState<'open' | 'closed' | 'all'>('open');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  useEffect(() => {
    const t = setTimeout(() => {
      setQ(search);
      setPage(1);
    }, 250);
    return () => clearTimeout(t);
  }, [search]);
  const params = new URLSearchParams({ state, page: String(page), pageSize: '20' });
  if (q) params.set('q', q);
  const { data, isLoading } = useQuery({ queryKey: ['portal', 'tickets', params.toString()], queryFn: () => api<Paginated<PortalTicketItem>>(`/portal/tickets?${params}`) });

  return (
    <>
      <PageHeader
        title={me?.companyWide ? `Anfragen von ${me.customer.name}` : 'Meine Anfragen'}
        description={me ? `Kundennummer ${me.customer.number}` : undefined}
        actions={
          me && me.areas.length > 0 && (
            <Button asChild>
              <Link to="/portal/new">
                <Plus /> Neue Anfrage
              </Link>
            </Button>
          )
        }
      />
      <PortalAppointmentsCard />
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="flex gap-1">
          {(
            [
              ['open', 'Offen'],
              ['closed', 'Abgeschlossen'],
              ['all', 'Alle'],
            ] as const
          ).map(([v, l]) => (
            <button
              key={v}
              onClick={() => {
                setState(v);
                setPage(1);
              }}
              className={cn('rounded-full border px-3 py-1 text-sm', state === v ? 'border-primary bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground')}
            >
              {l}
            </button>
          ))}
        </div>
        <div className="relative sm:ml-auto sm:w-64">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-8" placeholder="Suchen" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Anfragen durchsuchen" />
        </div>
      </div>
      <Card>
        {isLoading && <Skeleton className="m-4 h-32" />}
        {data?.items.length === 0 && (
          <div className="px-6 py-14 text-center text-sm text-muted-foreground">
            {state === 'open' ? 'Sie haben keine offenen Anfragen.' : 'Keine Anfragen gefunden.'}
          </div>
        )}
        <ul className="divide-y">
          {data?.items.map((t) => (
            <li key={t.id}>
              <Link to={`/portal/tickets/${t.id}`} className="flex items-center gap-3 px-4 py-3.5 hover:bg-muted/40">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-xs text-muted-foreground">{t.number}</span>
                    <PortalStatus status={t.status} isClosed={t.isClosed} />
                  </div>
                  <p className="mt-0.5 truncate font-medium">{t.subject}</p>
                  <p className="text-xs text-muted-foreground">
                    {t.area.name} · aktualisiert {formatRelative(t.lastActivityAt)}
                    {me?.companyWide && t.requesterName && ` · ${t.requesterName}`}
                  </p>
                </div>
                <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
              </Link>
            </li>
          ))}
        </ul>
        {data && <Pagination page={data.page} pageSize={data.pageSize} total={data.total} onPage={setPage} />}
      </Card>
    </>
  );
}

export function PortalNewTicketPage() {
  const { data: me } = usePortalMe();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [areaId, setAreaId] = useState('');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const { data: forms } = usePortalForms();

  const create = useMutation({
    mutationFn: async () => {
      const t = await api<{ id: string; number: string }>('/portal/tickets', { method: 'POST', body: { areaId, subject, description } });
      for (const file of files) {
        const form = new FormData();
        form.append('file', file);
        try {
          await api(`/portal/tickets/${t.id}/attachments`, { method: 'POST', form });
        } catch (e) {
          toast.error(`${file.name}: ${errorMessage(e)}`);
        }
      }
      return t;
    },
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: ['portal', 'tickets'] });
      toast.success(`Ihre Anfrage ${t.number} ist eingegangen`);
      navigate(`/portal/tickets/${t.id}`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  if (!me) return <Skeleton className="h-64" />;
  if (me.areas.length === 0 && !forms?.length) return <Alert>Für Ihr Kundenkonto sind derzeit keine Anfragebereiche freigeschaltet.</Alert>;
  const valid = areaId && subject.trim().length >= 3 && description.trim().length >= 10;

  return (
    <>
      <Link to="/portal" className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="size-4" /> Zurück
      </Link>
      <PageHeader title="Neue Anfrage" />
      {forms && forms.length > 0 && (
        <div className="mb-6">
          <p className="mb-2 text-sm font-medium">Mit einem Formular (empfohlen)</p>
          <div className="grid gap-2 sm:grid-cols-2">
            {forms.map((f) => (
              <Link key={f.slug} to={`/portal/forms/${f.slug}`} className="rounded-lg border bg-card p-4 transition-colors hover:border-primary/50 hover:bg-primary/5">
                <span className="block font-medium">{f.name}</span>
                {f.description && <span className="mt-1 block text-sm text-muted-foreground">{f.description}</span>}
              </Link>
            ))}
          </div>
          {me.areas.length > 0 && <p className="mb-2 mt-6 text-sm font-medium">Oder freie Anfrage</p>}
        </div>
      )}
      {me.areas.length > 0 && (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (valid) create.mutate();
        }}
      >
        <Card>
          <CardContent className="grid gap-5 pt-5">
            <fieldset>
              <legend className="mb-2 text-sm font-medium">Worum geht es?</legend>
              <div className="grid gap-2 sm:grid-cols-2">
                {me.areas.map((a) => (
                  <label
                    key={a.id}
                    className={cn('flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors', areaId === a.id ? 'border-primary bg-primary/5' : 'hover:bg-muted/40')}
                  >
                    <input type="radio" name="area" className="mt-1 accent-[var(--primary)]" checked={areaId === a.id} onChange={() => setAreaId(a.id)} />
                    <span>
                      <span className="block font-medium">{a.name}</span>
                      {a.description && <span className="block text-sm text-muted-foreground">{a.description}</span>}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
            <Field label="Betreff" htmlFor="pt-subject">
              <Input id="pt-subject" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200} placeholder="z. B. Drucker im Büro druckt nicht" />
            </Field>
            <Field label="Beschreibung" htmlFor="pt-desc" hint="Bitte beschreiben Sie das Problem möglichst genau: Was ist passiert, seit wann, welche Fehlermeldung?">
              <Textarea id="pt-desc" rows={7} value={description} onChange={(e) => setDescription(e.target.value)} />
            </Field>
            <div>
              <p className="mb-2 text-sm font-medium">Fotos und Dateien</p>
              <div className="flex flex-wrap items-center gap-2">
                <UploadButton label="Datei auswählen" onFile={(f) => setFiles((x) => [...x, f])} />
                {files.map((f, i) => (
                  <Badge key={i} variant="secondary" className="gap-1.5">
                    {f.name}
                    <button type="button" onClick={() => setFiles((x) => x.filter((_, j) => j !== i))} aria-label={`${f.name} entfernen`}>
                      ×
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
          <CardFooter>
            <Button type="submit" disabled={!valid} loading={create.isPending}>
              Anfrage senden
            </Button>
          </CardFooter>
        </Card>
      </form>
      )}
    </>
  );
}

export function PortalTicketPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const [reply, setReply] = useState('');
  const key = ['portal', 'ticket', id];
  const { data: t, error } = useQuery({ queryKey: key, queryFn: () => api<PortalTicketDetail>(`/portal/tickets/${id}`) });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: key });
    qc.invalidateQueries({ queryKey: ['portal', 'tickets'] });
  };
  const send = useMutation({
    mutationFn: () => api(`/portal/tickets/${id}/comments`, { method: 'POST', body: { body: reply } }),
    onSuccess: () => {
      setReply('');
      refresh();
      toast.success('Nachricht gesendet');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const upload = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return api(`/portal/tickets/${id}/attachments`, { method: 'POST', form });
    },
    onSuccess: () => {
      refresh();
      toast.success('Datei hochgeladen');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  if (error instanceof ApiError && error.status === 404) return <Alert>Diese Anfrage wurde nicht gefunden.</Alert>;
  if (!t) return <Skeleton className="h-96" />;

  return (
    <>
      <Link to="/portal" className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="size-4" /> Meine Anfragen
      </Link>
      <div className="mb-5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-sm text-muted-foreground">{t.number}</span>
          <PortalStatus status={t.status} isClosed={t.isClosed} />
          <Badge variant="outline">{t.area.name}</Badge>
        </div>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight">{t.subject}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Eingegangen am {formatDateTime(t.createdAt)}
          {t.assigneeName && ` · Bearbeitet von ${t.assigneeName}`}
          {t.dueAt && ` · Voraussichtlich bis ${formatDate(t.dueAt)}`}
        </p>
      </div>
      <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_16rem]">
        <div className="flex min-w-0 flex-col gap-3">
          <Card>
            <CardContent className="whitespace-pre-wrap break-words pt-5 text-sm">{t.description}</CardContent>
          </Card>
          {t.messages.map((m) => (
            <div key={m.id} className={cn('rounded-xl border p-4', m.fromCustomer ? 'ml-6 bg-primary/5 border-primary/20' : 'mr-6 bg-card')}>
              <p className="mb-1 text-sm">
                <span className="font-medium">{m.fromCustomer ? m.authorName ?? 'Sie' : m.authorName ?? 'Service-Team'}</span>{' '}
                <span className="text-xs text-muted-foreground">{formatRelative(m.createdAt)}</span>
              </p>
              <p className="whitespace-pre-wrap break-words text-sm">{m.body}</p>
            </div>
          ))}
          {t.isClosed ? (
            <Alert>Diese Anfrage ist abgeschlossen. Für ein neues Anliegen stellen Sie bitte eine neue Anfrage.</Alert>
          ) : (
            <Card>
              <CardContent className="pt-4">
                <Textarea rows={4} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Ihre Nachricht an das Service-Team …" aria-label="Nachricht" />
                <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                  <UploadButton onFile={(f) => upload.mutate(f)} loading={upload.isPending} label="Foto oder Datei" />
                  <Button onClick={() => send.mutate()} disabled={!reply.trim()} loading={send.isPending}>
                    Senden
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
        <aside className="flex flex-col gap-4">
          {t.attachments.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Dateien</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="flex flex-col gap-1.5 text-sm">
                  {t.attachments.map((a) => (
                    <li key={a.id}>
                      <a href={`/api/portal/tickets/${t.id}/attachments/${a.id}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:underline">
                        <Download className="size-4 shrink-0 text-muted-foreground" />
                        <span className="truncate">{a.name}</span>
                        <span className="shrink-0 text-xs text-muted-foreground">{formatBytes(a.size)}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
          {t.history && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Verlauf</CardTitle>
              </CardHeader>
              <CardContent>
                <ol className="flex flex-col gap-3 border-l pl-4">
                  {t.history.map((h, i) => (
                    <li key={i} className="relative text-sm">
                      <span className="absolute -left-[21px] top-1 size-2.5 rounded-full border-2 border-background bg-primary" />
                      <p>{h.label}</p>
                      <p className="text-xs text-muted-foreground">{formatDateTime(h.createdAt)}</p>
                    </li>
                  ))}
                </ol>
              </CardContent>
            </Card>
          )}
        </aside>
      </div>
    </>
  );
}

